import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, ArrowUp, Megaphone, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_LABELS } from "../listing/constants";

const PLACEMENT_LABELS = {
  top_banner: "Top Banner (Home)", sidebar_card: "Sidebar Card (Home)", mid_banner: "Mid-Page Banner (Home)",
  local_service_card: "Local Service (Home + Sponsors)", lower_banner: "Lower Banner (Home)",
};
const BANNER_TYPES = ["top_banner", "mid_banner", "lower_banner", "sidebar_card"];

export function SponsorPlacementsPanel({ job, onChanged }) {
  const [advertisers, setAdvertisers] = useState([]);
  const [placements, setPlacements] = useState(job.delivery?.sponsor_placements || []);
  const [draftAdvertiserId, setDraftAdvertiserId] = useState("");
  const [draftPlacementType, setDraftPlacementType] = useState("sidebar_card");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/advertisers").then((r) => setAdvertisers(r.data)).catch(() => {}); }, []);
  useEffect(() => { setPlacements(job.delivery?.sponsor_placements || []); }, [job.delivery?.sponsor_placements]);

  const byId = Object.fromEntries(advertisers.map((a) => [a.id, a]));
  const persist = async (next) => {
    setPlacements(next);
    setSaving(true);
    try {
      await api.put(`/admin/orders/${job.id}/sponsor-placements`, { placements: next.map((p) => ({ advertiser_id: p.advertiser_id, placement_type: p.placement_type })) });
      onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const add = () => {
    if (!draftAdvertiserId) { toast.error("Pick an advertiser first"); return; }
    persist([...placements, { advertiser_id: draftAdvertiserId, placement_type: draftPlacementType }]);
    setDraftAdvertiserId("");
  };
  const remove = (original) => persist(placements.filter((p) => p !== original));

  // Group placements so "who shows first" within a category (e.g. Premier
  // Agents) can be reordered on its own, instead of one long mixed list.
  const groups = [];
  for (const type of BANNER_TYPES) {
    const items = placements.filter((p) => p.placement_type === type);
    if (items.length) groups.push({ key: type, label: PLACEMENT_LABELS[type], items });
  }
  const localServiceItems = placements.filter((p) => p.placement_type === "local_service_card");
  const categoryOrder = Object.keys(CATEGORY_LABELS);
  for (const cat of categoryOrder) {
    const items = localServiceItems.filter((p) => byId[p.advertiser_id]?.category === cat);
    if (items.length) groups.push({ key: `local_service_card:${cat}`, label: `${CATEGORY_LABELS[cat]} (Home + Sponsors)`, items });
  }
  const uncategorized = localServiceItems.filter((p) => !byId[p.advertiser_id] || !categoryOrder.includes(byId[p.advertiser_id].category));
  if (uncategorized.length) groups.push({ key: "local_service_card:other", label: "Local Service (Home + Sponsors)", items: uncategorized });

  const moveWithinGroup = (groupItems, index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= groupItems.length) return;
    const reordered = [...groupItems];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    // Rebuild the full flat array: every other group keeps its current
    // relative order, only this group's items are swapped.
    const next = [];
    for (const g of groups) next.push(...(g.items === groupItems ? reordered : g.items));
    persist(next);
  };

  return (
    <div className="rounded-xl border border-[#E4E9F0] bg-white p-5" style={{ boxShadow: "0 3px 12px rgba(15,23,42,0.035)" }} data-testid="sponsor-placements-panel">
      <div className="flex items-center gap-2 text-[#0C2347]"><Megaphone size={15} /><div className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#475467]">Sponsor Placements</div></div>
      <p className="text-xs text-[#475467] mt-1">Assign local-service advertisers to this property website. The order within each group below controls who shows first on the public site (e.g. the first Real Estate Agent is who visitors see first). Manage the advertiser directory from the Advertisers page.</p>

      <div className="mt-4 space-y-5">
        {placements.length === 0 && <div data-testid="sponsor-placements-empty" className="text-xs text-[#94A3B8] py-3">No sponsors assigned to this property yet.</div>}
        {groups.map((group) => (
          <div key={group.key}>
            <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-1.5">{group.label}</div>
            <div className="space-y-2">
              {group.items.map((p, i) => {
                const ad = byId[p.advertiser_id];
                const globalIndex = placements.indexOf(p);
                return (
                  <div key={`${p.advertiser_id}-${p.placement_type}-${globalIndex}`} data-testid={`sponsor-placement-row-${globalIndex}`} className="flex items-center gap-3 rounded-lg border border-[#E4E9F0] px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-[#101828] truncate">{ad?.business_name || "Unknown advertiser"}</div>
                      {i === 0 && group.items.length > 1 && <div className="text-[10px] text-[#C7A361] font-mono uppercase tracking-[0.08em]">Shows first</div>}
                    </div>
                    <button data-testid={`sponsor-placement-up-${globalIndex}`} disabled={i === 0 || saving} onClick={() => moveWithinGroup(group.items, i, -1)} className="p-1.5 text-[#94A3B8] disabled:opacity-30"><ArrowUp size={14} /></button>
                    <button data-testid={`sponsor-placement-down-${globalIndex}`} disabled={i === group.items.length - 1 || saving} onClick={() => moveWithinGroup(group.items, i, 1)} className="p-1.5 text-[#94A3B8] disabled:opacity-30"><ArrowDown size={14} /></button>
                    <button data-testid={`sponsor-placement-remove-${globalIndex}`} disabled={saving} onClick={() => remove(p)} className="p-1.5 text-red-600"><Trash2 size={14} /></button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-[#E4E9F0] flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]">
          <Select value={draftAdvertiserId} onValueChange={setDraftAdvertiserId}>
            <SelectTrigger data-testid="sponsor-placement-advertiser-select" className="rounded-lg border-[#E4E9F0] h-10 text-sm"><SelectValue placeholder="Choose advertiser" /></SelectTrigger>
            <SelectContent>{advertisers.filter((a) => a.enabled !== false).map((a) => <SelectItem key={a.id} value={a.id}>{a.business_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Select value={draftPlacementType} onValueChange={setDraftPlacementType}>
            <SelectTrigger data-testid="sponsor-placement-type-select" className="rounded-lg border-[#E4E9F0] h-10 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(PLACEMENT_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="sponsor-placement-add" size="sm" disabled={saving} onClick={add} className="rounded-lg bg-[#0C2347] hover:bg-[#123262] text-white h-10 gap-1.5"><Plus size={14} />Add</Button>
      </div>
      {advertisers.length === 0 && <p className="text-xs text-[#94A3B8] mt-3">No advertisers in your directory yet — add one from the Advertisers page in the sidebar.</p>}
    </div>
  );
}
